import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
	Layout,
    Space,
    Radio,
    Checkbox,
    Button,
    Input,
    InputNumber,
    Typography,
    DatePicker,
    Spin,
    message,
    Form,
    Modal,
    Select,
    Col,
    Row,
    Divider
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { MERGE_ITEM_NAMES, UPDATE_ITEM } from '../../mutations';
import { GET_ITEM, GET_ITEM_NAME, GET_PAGINATED_ITEMS, ITEM_NAME_CONFLICT } from '../../queries';
import UploadButton from './UploadButton';
import { getApolloErrorMessage } from '../../Utilities/apolloErrorMessage';
import moment from 'moment';

const EDIT_ITEM_ERROR_FALLBACK = '商品の編集に失敗しました。';

/** Merge live form store over submit-time values without clobbering with undefined keys. */
function mergePendingFormFields(pending, live) {
    const out = { ...(pending || {}) };
    if (!live) return out;
    Object.keys(live).forEach((k) => {
        if (live[k] !== undefined) out[k] = live[k];
    });
    return out;
}

function pickNonBlankString(formVal, initialVal, serverVal) {
    if (formVal != null && String(formVal).trim() !== '') {
        return String(formVal).trim();
    }
    if (initialVal != null && String(initialVal).trim() !== '') {
        return String(initialVal).trim();
    }
    if (serverVal != null && String(serverVal).trim() !== '') {
        return String(serverVal).trim();
    }
    return '';
}

const EditItem = ({ handleFinishEdit, itemNameId, itemNames, department: activeDepartment }) => {
    const MERGE_SEARCH_DEBOUNCE_MS = 300;
    const MERGE_SEARCH_MIN_LENGTH = 2;
    const MERGE_SEARCH_LIMIT = 30;
    const apolloClient = useApolloClient();
    const { data, loading } = useQuery(GET_ITEM, { variables: { id: itemNameId }, fetchPolicy: 'no-cache'});
    const [itemImgFile, setItemImgFile] = useState('')
    const [form] = Form.useForm();
    const pendingFormValuesRef = useRef(null);
    const [dupeError, setDupeError] = useState(false)
    const [box, setBox] = useState()
    const [modalOpen, setModalOpen] = useState(false)
    const [confirmSubmitLoading, setConfirmSubmitLoading] = useState(false)
    const [nameConflictModalOpen, setNameConflictModalOpen] = useState(false)
    const [pendingConflictRecipientId, setPendingConflictRecipientId] = useState(null)
    const [showMerge, setShowMerge] = useState(false)
    const [mergeSearchTerm, setMergeSearchTerm] = useState('');
    const [debouncedMergeSearchTerm, setDebouncedMergeSearchTerm] = useState('');
    const [remoteMergeOptions, setRemoteMergeOptions] = useState([]);
    const [mergeCandidatesLoading, setMergeCandidatesLoading] = useState(false);
    const [selectedMergeOption, setSelectedMergeOption] = useState(null);

    const [mergeItems] = useMutation(MERGE_ITEM_NAMES, {
        onCompleted: (data) => {
            message.success('商品をマージしました。');
            handleFinishEdit();
        },
        onError: (error) => {
            console.log(error);
            message.error('商品のマージに失敗しました。', 5);
        }
    })
    
    const [mergeForm] = Form.useForm()
    const watchedMergeItemId = Form.useWatch('mergeItemId', mergeForm);

    useEffect(() => {
        if (!showMerge) {
            setMergeSearchTerm('');
            setDebouncedMergeSearchTerm('');
            setMergeCandidatesLoading(false);
            return;
        }
        const timerId = setTimeout(() => {
            setDebouncedMergeSearchTerm(mergeSearchTerm.trim());
        }, MERGE_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timerId);
    }, [showMerge, mergeSearchTerm]);

    const shouldRunMergeSearch =
        showMerge &&
        (debouncedMergeSearchTerm.length === 0 || debouncedMergeSearchTerm.length >= MERGE_SEARCH_MIN_LENGTH);

    useEffect(() => {
        let cancelled = false;
        if (!shouldRunMergeSearch) {
            setRemoteMergeOptions([]);
            setMergeCandidatesLoading(false);
            return;
        }

        const loadMergeCandidates = async () => {
            setMergeCandidatesLoading(true);
            try {
                const result = await apolloClient.query({
                    query: GET_PAGINATED_ITEMS,
                    variables: {
                        department: activeDepartment || null,
                        page: 1,
                        perPage: MERGE_SEARCH_LIMIT,
                        search: debouncedMergeSearchTerm || null,
                        favoritesOnly: false,
                        currentMonth: false,
                        showRegularItems: true,
                        tagIds: null,
                        hasParentProdId: false,
                    },
                    fetchPolicy: 'network-only',
                });
                if (cancelled) return;

                const payload = result?.data?.paginatedItems;
                const parsed = typeof payload === 'string'
                    ? JSON.parse(payload)
                    : (payload && typeof payload === 'object' ? payload : null);

                let options = (parsed?.items || []).map(item => ({
                    value: String(item.id),
                    label: `${item.item_name} : ${item.item_size || ''}`,
                    itemName: item.item_name,
                    itemSize: item.item_size || '',
                }));

                const trimmed = debouncedMergeSearchTerm.trim();
                const numericIdSearch = /^\d+$/.test(trimmed);
                if (options.length === 0 && numericIdSearch) {
                    try {
                        const byIdResult = await apolloClient.query({
                            query: GET_ITEM_NAME,
                            variables: { itemNameId: parseInt(trimmed, 10) },
                            fetchPolicy: 'network-only',
                        });
                        const node = byIdResult?.data?.itemName;
                        if (node) {
                            options = [{
                                value: String(node.id),
                                label: `${node.itemName} : ${node.itemSize || ''}`,
                                itemName: node.itemName,
                                itemSize: node.itemSize || '',
                            }];
                        }
                    } catch (idError) {
                        // keep empty options for non-existing IDs or access-restricted records
                        console.log(idError);
                    }
                }

                setRemoteMergeOptions(options);
            } catch (error) {
                if (!cancelled) {
                    console.log(error);
                    setRemoteMergeOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setMergeCandidatesLoading(false);
                }
            }
        };

        loadMergeCandidates();
        return () => {
            cancelled = true;
            setMergeCandidatesLoading(false);
        };
    }, [shouldRunMergeSearch, debouncedMergeSearchTerm, activeDepartment, apolloClient]);

    const mergeOptions = useMemo(() => {
        const currentId = String(itemNameId);
        const optionMap = new Map();
        remoteMergeOptions.forEach(option => {
            const value = String(option.value);
            if (value === currentId) return;
            optionMap.set(value, option);
        });

        (itemNames || []).forEach(item => {
            const value = String(item.key);
            if (value === currentId) return;
            if (!optionMap.has(value)) {
                optionMap.set(value, {
                    value,
                    label: `${item.name} : ${item.size || ''}`,
                    itemName: item.name,
                    itemSize: item.size || '',
                });
            }
        });

        if (selectedMergeOption && selectedMergeOption.value !== currentId && !optionMap.has(selectedMergeOption.value)) {
            optionMap.set(selectedMergeOption.value, selectedMergeOption);
        }

        return Array.from(optionMap.values());
    }, [itemNameId, itemNames, remoteMergeOptions, selectedMergeOption]);

    useEffect(() => {
        if (watchedMergeItemId == null || watchedMergeItemId === '') {
            setSelectedMergeOption(null);
            return;
        }
        const watchedValue = String(watchedMergeItemId);
        const existing = mergeOptions.find(option => option.value === watchedValue);
        if (existing) {
            setSelectedMergeOption(existing);
        } else {
            setSelectedMergeOption({ value: watchedValue, label: `ID: ${watchedValue}` });
        }
    }, [watchedMergeItemId, mergeOptions]);
    
    const [updateItem] = useMutation(UPDATE_ITEM, {
        onCompleted: (data) => {
            message.success('商品を編集しました。');
            form.resetFields();
            handleFinishEdit();
        },
        onError: (error) => {
            console.log(error);
            const graphQLErrors = error.graphQLErrors || [];
            const ge = graphQLErrors[0];
            if (ge && ge.extensions && ge.extensions.code === 'ITEM_NAME_UNIQUE_VIOLATION') {
                message.warning(ge.message, 8);
                const rid = ge.extensions.conflicting_item_name_id;
                if (rid != null) {
                    setShowMerge(true);
                    mergeForm.setFieldsValue({ mergeItemId: String(rid) });
                }
                return;
            }
            message.error(
                getApolloErrorMessage(error, { fallback: EDIT_ITEM_ERROR_FALLBACK }),
                8
            );
        }
    });

    const goToMergeFromConflict = () => {
        setNameConflictModalOpen(false);
        setShowMerge(true);
        if (pendingConflictRecipientId != null) {
            mergeForm.setFieldsValue({ mergeItemId: String(pendingConflictRecipientId) });
        }
        setPendingConflictRecipientId(null);
    };

    const onConfirmModalUpdate = async () => {
        setConfirmSubmitLoading(true);
        try {
            try {
                await form.validateFields();
            } catch {
                return;
            }
            const fields = mergePendingFormFields(pendingFormValuesRef.current, form.getFieldsValue(true));
            pendingFormValuesRef.current = null;
            const row0 = data?.item?.item_names?.[0];
            const nameFromServer = row0?.item_name ?? row0?.itemName;
            const sizeFromServer = row0?.item_size ?? row0?.itemSize;
            const resolvedItemName = pickNonBlankString(fields.itemName, initialValues.itemName, nameFromServer);
            const itemSize = fields.itemSize ?? initialValues.itemSize ?? sizeFromServer;
            const boxSize = fields.boxSize ?? initialValues.boxSize;
            const regular = fields.regular ?? initialValues.regular;
            const itemMappings = fields.itemMappings ?? initialValues.itemMappings;
            const profile0 = data?.item?.item_profiles?.[0];
            const detailFromServer = profile0?.item_detail ?? profile0?.itemDetail;
            const itemDetail =
                fields.itemDetail ??
                form.getFieldValue('itemDetail') ??
                initialValues.itemDetail ??
                detailFromServer ??
                '';
            const itemSellingCycles = fields.itemSellingCycles ?? initialValues.itemSellingCycles;
            const parentProdIds = fields.parentProdIds ?? initialValues.parentProdIds;
            const userDefinedStatus = regular ? 'Regular' : null;
            let resolvedItemSize;
            if (itemSize === '箱') {
                const n = Number(boxSize);
                if (boxSize === undefined || boxSize === null || boxSize === '' || !Number.isFinite(n) || n <= 0) {
                    message.error('箱を選んだ場合は個数（1以上）が必要です。', 5);
                    return;
                }
                resolvedItemSize = String(Math.trunc(n));
            } else {
                resolvedItemSize = itemSize;
            }
            // Always include string keys so JSON.stringify does not drop undefined (which omits keys).
            const itemNamePayload = {
                id: initialValues.id,
                itemName: resolvedItemName,
                itemSize: resolvedItemSize,
                userDefinedStatus: userDefinedStatus ?? null,
            };
            // updateItemInput expects arrays for itemSellingCycles / itemMappings (GraphQL [JSON!]!).
            // Network tab: input.itemProfile is JSON with camelCase keys (itemDetail, itemImgFile).
            let inputVariables = {
                itemName: itemNamePayload,
                itemProfile: {
                    itemDetail: itemDetail,
                },
                itemSellingCycles: [],
                itemMappings: [],
            };

            if (itemSellingCycles.length > 0) {
                inputVariables.itemSellingCycles = itemSellingCycles.map(sc => {
                    return {
                        regular_price: parseInt(sc.price),
                        selling_start_dt: sc.dates[0].format('YYYY-MM-DD'),
                        selling_end_dt: sc.dates[1].format('YYYY-MM-DD')
                    }
                })
            }

            // Sweets-only fields follow the loaded row's department (not the viewer's multi-dept list).
            const showSweetsFeatures = initialValues.department === 'sweets';
            const codes = showSweetsFeatures
                ? (parentProdIds || []).map(row => (row && row.code != null) ? row.code : null).filter(c => c != null)
                : [];
            const hasProdMappings = itemMappings && itemMappings.length > 0;

            if (hasProdMappings) {
                const seen = {}
                setDupeError(false)
                let dupe = false
                itemMappings.forEach(e => {
                    if (e.prod_id == null && e.prod_name == null) return
                    const concatenated = e.prod_id.toString() + (e.prod_name || "")
                    if (seen[e.prod_id] === concatenated) {
                        dupe = true
                    } else {
                        seen[e.prod_id] = concatenated
                    }
                })
                if (dupe) {
                    setDupeError(true)
                    message.error('製品コードが重複しています。', 5);
                    return
                }
                // Sweets: parent codes come only from 集約親管理コード (parentProdIds). Clear nested
                // parent_prod_id on 製品 rows so replacing the list does not re-send stale codes.
                const fullMappings = showSweetsFeatures
                    ? itemMappings.map(m => ({ ...m, parent_prod_id: null }))
                    : itemMappings.map(m => ({ ...m, parent_prod_id: m.parent_prod_id ?? null }));
                const parentOnlyMappings = codes.map(code => ({ prod_id: null, prod_name: null, product_category_id: null, parent_prod_id: code }));
                inputVariables.itemMappings = [...fullMappings, ...parentOnlyMappings]
            } else if (codes.length > 0) {
                // Only 集約親管理コード, no 製品 rows
                inputVariables.itemMappings = codes.map(code => ({ prod_id: null, prod_name: null, product_category_id: null, parent_prod_id: code }))
            } else {
                message.error('製品または集約親管理コードを少なくとも1つ追加してください。', 5);
                return
            }
            if (itemImgFile) {
                inputVariables.itemProfile.itemImgFile = itemImgFile
            }

            const inItemName = itemNamePayload;
            const itemSizeForQuery = inItemName.itemSize === undefined || inItemName.itemSize === null
                ? null
                : String(inItemName.itemSize);

            let conflictResult;
            try {
                conflictResult = await apolloClient.query({
                    query: ITEM_NAME_CONFLICT,
                    variables: {
                        itemName: inItemName.itemName,
                        itemSize: itemSizeForQuery,
                        excludeId: String(inItemName.id),
                    },
                    fetchPolicy: 'network-only',
                });
            } catch (error) {
                console.log(error);
                message.error('重複確認に失敗しました。', 5);
                return;
            }

            const conflict = conflictResult?.data?.itemNameConflict;
            if (conflict && conflict.id != null) {
                setModalOpen(false);
                setPendingConflictRecipientId(conflict.id);
                setNameConflictModalOpen(true);
                return;
            }

            updateItem({ variables: { input: inputVariables }});
        } finally {
            setConfirmSubmitLoading(false);
        }
    };
    
    useEffect(() => {
        window.history.pushState(null, null, document.URL);
        window.addEventListener('popstate', event => {
            handleFinishEdit();
        });
    }, [handleFinishEdit]);

    useEffect(() => {
        if(data) {
            if(data.item.item_names[0].item_size > 0) { // I hope this will always correctly evaluate if it's a non-zero number 
                setBox(true)
            }
        }
    }, [data])

    if (loading) {
        return <Spin />
    }

    const formItemLayout = {
        labelCol:  {
            xs: { span: 24 },
            sm: { span: 24 },
            md:  { span: 24 },
            lg:  { span: 6 }
        },
        wrapperCol: {
            xs: { span: 24 },
            sm: { span: 24 },
            md:  { span: 24 },
            lg:  { span: 8 }
        },
        layout: 'horizontal',
        labelAlign: 'right'
    };

    const options = [
        { label: "バラ", value: "" },
        { label: "SS", value: "SS" },
        { label: "S", value: "S" },
        { label: "M", value: "M" },
        { label: "L", value: "L" },
        { label: "XL", value: "XL" },
        { label: "箱", value: "箱" },
    ];

    let editItemName;
    let editItemMappings;
    let editItemProfiles;
    let editItemSellingCycles;
    let initialValues;
    if (data) {
        editItemName = data.item.item_names[0]
        const loadedItemName = editItemName.item_name ?? editItemName.itemName
        const loadedItemSize = editItemName.item_size ?? editItemName.itemSize ?? ''
        editItemMappings = data.item.item_mappings
        editItemProfiles = data.item.item_profiles[0]
        editItemSellingCycles = data.item.item_selling_cycles.map(sc => {
            return { price: sc.regular_price, dates: [moment(sc.selling_start_dt), moment(sc.selling_end_dt)] }
        })
        const rowDepartment = editItemName.department || activeDepartment || '';
        const isSweetsItem = rowDepartment === 'sweets';
        // 集約親管理コード list: all registered parent_prod_id from any mapping (order preserved, distinct)
        const seenParent = new Set();
        const parentProdIdsInitial = (editItemMappings || [])
            .map(m => m.parent_prod_id)
            .filter(v => v != null && v !== '' && !seenParent.has(v) && (seenParent.add(v), true));
        // 製品 list: mappings with prod_id or prod_name. For sweets, strip nested parent_prod_id
        // so the separate parentProdIds list is the single source of truth on save.
        const prodMappingsOnly = (editItemMappings || [])
            .filter(m => m.prod_id != null || m.prod_name != null)
            .map(m => (isSweetsItem ? { ...m, parent_prod_id: null } : m));
        const sizeForForm = isNaN(loadedItemSize) || loadedItemSize === '' ? loadedItemSize : '箱';
        initialValues = {
            id: editItemName.id,
            department: rowDepartment,
            downloadUrl: data.item.download_url ?? data.item.downloadUrl,
            itemName: loadedItemName,
            itemSize: sizeForForm,
            regular: editItemName.user_defined_status === "Regular" || editItemName.userDefinedStatus === "Regular"
                || editItemName.status === "Regular",
            itemDetail: editItemProfiles?.item_detail ?? editItemProfiles?.itemDetail,
            itemImgFile: editItemProfiles?.item_img_file ?? editItemProfiles?.itemImgFile,
            regularPrice: editItemSellingCycles?.regular_price ?? editItemSellingCycles?.regularPrice,
            itemMappings: prodMappingsOnly.length > 0 ? prodMappingsOnly : [],
            parentProdIds: isSweetsItem && parentProdIdsInitial.length > 0 ? parentProdIdsInitial.map(code => ({ code })) : [],
            itemSellingCycles: editItemSellingCycles
        }
        if (initialValues.itemSize === '箱') {
            initialValues.boxSize = loadedItemSize
        }
    }

    const itemRowDepartment = initialValues?.department ?? '';
    const isSweetsRow = itemRowDepartment === 'sweets';
    const isSnacksRow = itemRowDepartment === 'snacks';

    const handleMerge = () => {
        const recipientItemNameId = parseInt(mergeForm.getFieldValue('mergeItemId'), 10);
        if (Number.isNaN(recipientItemNameId)) {
            message.error('マージ対象を選択してください。', 5);
            return;
        }
        const duplicateItemNameId = initialValues.id
        mergeItems({ variables: { input: { recipientItemNameId, duplicateItemNameId }}})
    }
        
    
    return <>
        <Modal open={modalOpen} title="商品内容の変更" okText="変更する" cancelText="キャンセル" onOk={onConfirmModalUpdate} onCancel={() => { pendingFormValuesRef.current = null; setModalOpen(false); }} confirmLoading={confirmSubmitLoading}>
            商品内容を変更します。
            よろしいですか。
        </Modal>
        <Modal
            open={nameConflictModalOpen}
            title="商品名・サイズが重複しています"
            onCancel={() => {
                setNameConflictModalOpen(false);
                setPendingConflictRecipientId(null);
            }}
            footer={[
                <Button key="close" onClick={() => {
                    setNameConflictModalOpen(false);
                    setPendingConflictRecipientId(null);
                }}>閉じる</Button>,
                <Button key="merge" type="primary" onClick={goToMergeFromConflict}>マージ画面へ</Button>,
            ]}
        >
            <Typography.Paragraph>
                別の商品が同じ商品名・サイズの組み合わせで既に登録されています。先にマージするか、商品名・サイズを変更してください。
            </Typography.Paragraph>
        </Modal>
        <Layout style={{ padding: '24px 24px 0px 24px', background: '#fff' }}>
            <Typography.Title level={4}>商品内容</Typography.Title>
        </Layout>
        <Layout style={{ padding: 24, background: '#fff' }}>
            <Form
                form={form}
                onFinish={(values) => {
                    pendingFormValuesRef.current = values;
                    setModalOpen(true);
                }}
                initialValues={initialValues}
                {...formItemLayout}
            >
                <Form.Item label='商品名' name='itemName' 
                    rules={[
                        { required: true, message: '商品名が必要です。' }, 
                        { whitespace: true, message: '商品名が必要です。' }, 
                    ]}
                    required>
                    <Input placeholder="商品名" />
                </Form.Item>
                <Form.Item label='商品説明' name='itemDetail' required rules={[{ max: 1000, required: true, message: '商品説明は１０００文字以下である必要があります。' }]}>
                    <Input.TextArea placeholder="商品の説明をわかりやすく書いてください。 最大 1000文字入力可能です。"/>
                </Form.Item>
				<Form.Item label='製品' name='itemMappings' rules={[]} {...dupeError && {validateStatus: "error"}} dependencies={['itemMappings', 0]}>
					<Form.List name="itemMappings">
						{(fields, { add, remove }) => (
							<>
								{fields.map(({ key, name, fieldKey, ...restField }) => (
									<Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
										<Form.Item
											{...restField}
											name={[name, 'prod_id']}
											fieldKey={[fieldKey, 'prod_id']}
											rules={[{ required: true, message: '製品コードが必要です。' }]}
										>
											<InputNumber
												formatter={value => Math.abs(parseInt(value)) || null}
												placeholder="製品コード"
												type="number"
											/>
										</Form.Item>
										<Form.Item
											{...restField}
											name={[name, 'prod_name']}
											fieldKey={[fieldKey, 'prod_name']}
											rules={[{ required: true, message: '製品名(ｶﾅ)が必要です。' },
											{type: 'string', pattern: /^[\uFF61-\uFF9F0-9a-zA-Z+\-/*_~!@#$%^&(){}:;"'><.,`]+$/, message: '製品名は半角カタカナか英数字のみ入力可能です。'}]}
										>
											<Input placeholder="製品名" />
										</Form.Item>
										<Form.Item
											{...restField}
											name={[name, 'product_category_id']}
											fieldKey={[fieldKey, 'product_category_id']}
											rules={[{ required: true, message: '製品カテゴリーが必要です。' }]}
										>
											<InputNumber
												formatter={value => Math.abs(parseInt(value)) || null}
												placeholder="製品カテゴリー"
												type="number"
												style={{ width: 120 }}
											/>
										</Form.Item>
										<MinusCircleOutlined onClick={() => remove(name)} />
									</Space>
								))}
								<Form.Item>
									<Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
										製品を追加する
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>
				</Form.Item>
				{isSweetsRow && (
				<Form.Item label='集約親管理コード' name='parentProdIds'>
					<Form.List name="parentProdIds">
						{(fields, { add, remove }) => (
							<>
								{fields.map(({ key, name, fieldKey, ...restField }) => (
									<Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
										<Form.Item
											{...restField}
											name={[name, 'code']}
											fieldKey={[fieldKey, 'code']}
										>
											<InputNumber
												formatter={value => value != null ? Math.abs(parseInt(value)) || null : null}
												placeholder="集約親管理コード"
												type="number"
												style={{ width: 200 }}
												min={1}
											/>
										</Form.Item>
										<MinusCircleOutlined onClick={() => remove(name)} />
									</Space>
								))}
								<Form.Item>
									<Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
										集約管理コードを追加する
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>
				</Form.Item>
				)}
                <Form.Item label='販売期間 / 価格（税込）' name='itemSellingCycles' rules={[{ required: true, message: '販売期間と価格が必要です。' }]} dependencies={['itemSellingCycles', 0]}>
                    <Form.List name='itemSellingCycles'>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, fieldKey, ...restField }) => (
                                    <Space key={key} align="start">
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'dates']}
                                            fieldKey={[fieldKey, 'dates']}
                                            rules={[{ required: true, message: '日付が必要です。' }]}
                                        >
                                            <DatePicker.RangePicker />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'price']}
                                            fieldKey={[fieldKey, 'price']}
                                            rules={[{ required: true, message: '価格が必要です。' }]}
                                        >
                                            <Input
                                                type='number'
                                                placeholder="価格" min={0}
                                                step='any' addonAfter='円'
                                                style={{ width: 130 }}
                                            />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: 8 }} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        販売期間と価格を追加する
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Form.Item>
                <Form.Item label="定番商品" name='regular' valuePropName="checked">
                    <Checkbox />
                </Form.Item>
                {isSweetsRow && (
                    <>
                        <Form.Item label='サイズ' name="itemSize">
                            <Radio.Group options={options} onChange={e => e.target.value === "箱" ? setBox(true) : setBox(false)} />
                        </Form.Item>
                        <Form.Item
                            label='個数'
                            name="boxSize"
                            dependencies={['itemSize']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (getFieldValue('itemSize') !== '箱') {
                                            return Promise.resolve();
                                        }
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.reject(new Error('箱を選んだ場合は個数が必要です。'));
                                        }
                                        const n = Number(value);
                                        if (!Number.isFinite(n) || n <= 0) {
                                            return Promise.reject(new Error('個数は1以上の数を入力してください。'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <Input type='number' min={1} addonAfter='個' placeholder="個数" style={{ maxWidth: '8rem' }} disabled={!box} />
                        </Form.Item>
                    </>
                )}
                {isSnacksRow && (
                    <Form.Item label='サイズ' name="itemSize">
                        <Input readOnly bordered={false} />
                    </Form.Item>
                )}
                <Form.Item data-testid='add-image-btn' label='商品画像(ファイル形式: jpeg/pngのみ)' required>
                    <UploadButton
                        currentImgUrl={initialValues?.downloadUrl}
                        setItemImgFile={setItemImgFile}
                    />
                </Form.Item>
                <Form.Item colon={false} label=' '>
                    <Space>
                        <Button
                            type='primary' htmlType='submit' data-testid='submit-edit-item'
                        >
                            変更する
                        </Button>
                        <Button onClick={handleFinishEdit}>キャンセル</Button>
                    </Space>
                </Form.Item>
            </Form>
            <Divider style={{ margin: "1rem 0" }} />
            <Space style={{ marginLeft: '0.5rem' }}>
                <Checkbox onChange={e => setShowMerge(e.target.checked)}>マージセレクターを開く</Checkbox>
            </Space>
            {showMerge && (
                <Form form={mergeForm}>
                <Row>
                    <Col span={24}>
                        <Typography.Text strong>
                            「{editItemName.item_name}」は削除され、選択されたアイテムにマージ(結合)されます。
                        </Typography.Text>
                    </Col>
                </Row>
                <Row style={{marginBottom: ".5rem"}}>
                    <Col span={15} style={{ marginLeft: "1rem"}}>
                        <Form.Item label="マージ対象" name="mergeItemId" style={{ marginBottom: 0}}>
                            <Select
                                showSearch
                                filterOption={false}
                                onSearch={value => setMergeSearchTerm(value)}
                                onChange={value => {
                                    const selected = mergeOptions.find(option => option.value === String(value));
                                    if (selected) setSelectedMergeOption(selected);
                                }}
                                notFoundContent={
                                    mergeSearchTerm.trim().length > 0 && mergeSearchTerm.trim().length < MERGE_SEARCH_MIN_LENGTH
                                        ? `${MERGE_SEARCH_MIN_LENGTH}文字以上入力してください`
                                        : (mergeCandidatesLoading ? <Spin size="small" /> : null)
                                }
                            >
                                {mergeOptions.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <svg width="100%" height="150" viewBox="0 0 300 150">
                            <text x="150" y="30" textAnchor="middle" fill="black"><tspan style={{textDecoration: "line-through"}}>{editItemName.item_name}</tspan></text>
                            <rect x="85" y="90" width="130" height="30" fill="lightblue" />
                            <text x="150" y="110" textAnchor="middle" fill="black"> 選択されたアイテム</text>
                            <line x1="150" y1="40" x2="150" y2="70" stroke="black" strokeWidth="2" markerEnd="url(#arrow)" />
                            <defs>
                                <marker id="arrow" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
                                    <path d="M0,0 L0,6 L9,3 z" fill="black" />
                                </marker>
                            </defs>
                        </svg>
                    </Col>
                </Row>
                <Row>
                    <Col span={6}>
                        <Button type="primary" onClick={handleMerge}>マージ</Button>   
                    </Col>
                </Row>
                </Form>
            )}
        </Layout>
    </>
};

export default EditItem;
